import React from 'react';

// THE RESYNC SELECTION CRITERIA — the kinds and the date ranges each offers — shared by the single-pipeline ResyncModal
// and the BulkResyncModal, so both offer exactly the same searches. The parent owns the state (kind + ranges) and does
// the start; this only draws the choice and carries the helpers that read it.
//
// These are the LIVE OHIP reservation search (GET /rsv/v1/hotels/{hotelId}/reservations), the same endpoint the sync
// `ohip-reservation-request-by-dates` step uses — immediate, no throttle. That endpoint filters by arrival/stay only; it
// has no created/modified-date search, and OHIP's asynchronous change-summary API is throttled (one identical request
// per 30 min), so there is no Updated/Created date resync. Blocks has no proven date search yet, so it is offered but
// cannot be run (the server stops it with a reason).
export const KINDS = [
    {
        key: 'reservations', label: 'Reservations',
        ranges: [
            { key: 'arrival', label: 'Arrival date', help: 'Reservations arriving in the range.' },
            { key: 'stay', label: 'Stay date', help: 'Reservations in-house at any point in the range.' },
        ],
    },
    {
        key: 'profiles', label: 'Profiles',
        ranges: [
            { key: 'stay', label: 'Stay date', help: 'Guests, companies and agents on stays in the range.' },
        ],
    },
    {
        key: 'blocks', label: 'Blocks',
        available: false,
        ranges: [
            { key: 'begin', label: 'Begin (arrival) date' },
            { key: 'stay', label: 'Stay date' },
        ],
    },
];

export const kindDefOf = (kind) => KINDS.find(k => k.key === kind) || KINDS[0];

// Only the ranges the chosen kind offers, both ends filled — what goes to the server.
export const filledRangesOf = (kind, ranges) => kindDefOf(kind).ranges.reduce((acc, r) => {
    const v = ranges[r.key];
    if (v && v.from && v.to) acc[r.key] = { from: v.from, to: v.to };
    return acc;
}, {});

// A range with From after To is a mistake the server would 400 on — caught here so the button can be stopped.
export const anyBadOrderOf = (kind, ranges) => kindDefOf(kind).ranges.some(r => {
    const v = ranges[r.key];
    return v && v.from && v.to && v.from > v.to;
});

export default function ResyncFields({ kind, ranges, onKind, onRange, disabled }) {
    const kindDef = kindDefOf(kind);
    const available = kindDef.available !== false;
    return (
        <>
            <div className="form-group" style={{ marginBottom: '1rem' }}>
                <label htmlFor="resync-kind">What to resync</label>
                <select id="resync-kind" value={kind} disabled={disabled} onChange={e => onKind(e.target.value)}>
                    {KINDS.map(k => <option key={k.key} value={k.key}>{k.label}</option>)}
                </select>
            </div>

            {!available ? (
                <p className="cm-message" style={{ color: '#b45309' }}>
                    Block resync is not available for this source yet. Reservations and profiles can be resynced now.
                </p>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    {kindDef.ranges.map(r => {
                        const v = ranges[r.key] || {};
                        return (
                            <div key={r.key} style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: '0.75rem 1rem' }}>
                                <div style={{ fontWeight: 600, color: '#111827', marginBottom: '0.4rem' }}>{r.label}</div>
                                {r.help && <div style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '0.5rem' }}>{r.help}</div>}
                                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                                    <label style={{ fontSize: '0.8rem', color: '#374151' }}>
                                        From<br />
                                        <input type="date" value={v.from || ''} disabled={disabled} onChange={e => onRange(r.key, 'from', e.target.value)} />
                                    </label>
                                    <label style={{ fontSize: '0.8rem', color: '#374151' }}>
                                        To<br />
                                        <input type="date" value={v.to || ''} disabled={disabled} onChange={e => onRange(r.key, 'to', e.target.value)} />
                                    </label>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </>
    );
}
